import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  HardDrive, Smartphone, Tablet, Monitor, AlertTriangle,
  CheckCircle, ArrowRight, Phone, Shield, ChevronRight,
} from '../../lib/icons';
import SEOHead from '../../components/SEOHead';

const DEVICES = [
  { icon: Smartphone, label: 'iPhone', sub: 'All models — liquid damage, boot loop, failed restore' },
  { icon: Tablet, label: 'iPad', sub: 'All models — liquid damage, cracked, won\'t power on' },
  { icon: Monitor, label: 'MacBook', sub: 'Air & Pro — failed SSD, liquid damage, no power' },
  { icon: HardDrive, label: 'External Drive', sub: 'USB drives, hard drives, NAS — accidental deletion, corruption' },
  { icon: HardDrive, label: 'SSD', sub: 'Internal & external SSDs — failed, not detected' },
  { icon: HardDrive, label: 'Flash Drive', sub: 'USB flash drives — corrupted, not recognized, deleted files' },
];

const RECOVERABLE = [
  'Photos and videos',
  'Contacts and messages',
  'Documents and files',
  'App data and backups',
  'Notes and calendar data',
  'Music and media libraries',
];

const PROCESS = [
  {
    step: '1',
    title: 'Bring It In',
    body: 'Drop off your device at our Temecula location or contact us to arrange North County SD service. Do NOT attempt to charge, restore, or use recovery apps first — this can overwrite recoverable data.',
    color: '#0EA5E9',
  },
  {
    step: '2',
    title: 'Honest Diagnostic',
    body: 'We assess the device and tell you honestly what\'s recoverable and what isn\'t. Diagnostic fee applies ($29–$49 depending on device). If recovery isn\'t viable, we\'ll say so upfront.',
    color: '#10B981',
  },
  {
    step: '3',
    title: 'Recovery & Return',
    body: 'If recovery is viable, we proceed with a quoted price. Recovered files are delivered on a USB drive or transferred directly. No recovery = no repair charge beyond the diagnostic.',
    color: '#8B5CF6',
  },
];

const FAQS = [
  {
    q: 'Can you guarantee data recovery?',
    a: 'No — and we never will. Data recovery success depends on the extent of damage, how the device was treated afterward, and the type of storage. We\'ll give you an honest assessment after the diagnostic.',
  },
  {
    q: 'Should I try to turn on my water-damaged device first?',
    a: 'No. Powering on a wet device can cause additional short-circuit damage. Bring it in as-is, powered off. The sooner, the better.',
  },
  {
    q: 'My iPhone won\'t turn on — is my data gone?',
    a: 'Not necessarily. Devices that won\'t power on may still have recoverable storage. A diagnostic will determine whether recovery is possible.',
  },
  {
    q: 'How long does data recovery take?',
    a: 'The diagnostic is usually completed in 1–2 hours. Full recovery, depending on complexity, may take 1–5 days.',
  },
  {
    q: 'What does data recovery cost?',
    a: 'Pricing depends on device type and severity of the data loss. We provide a quote after the diagnostic. If we can\'t recover anything, you pay only the diagnostic fee.',
  },
];

const DataRecoveryPage = () => {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [form, setForm] = useState({ name: '', phone: '', device: '', issue: '' });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <div className="bg-[#050B14] min-h-screen font-sans">
      <SEOHead
        title="Data Recovery — iPhone, iPad, MacBook & SSD | GameFix Solutions"
        description="Data recovery from iPhones, iPads, MacBooks, SSDs, and external drives in Temecula, CA and North County San Diego. Water damage, deleted files, boot loop. Honest assessment — no recovery, no charge."
        keywords="data recovery temecula, iphone data recovery, ipad data recovery, macbook data recovery, ssd data recovery, water damage recovery temecula"
        canonical="https://gamefixsolutions.com/phone-repair/data-recovery"
      />

      {/* ── Hero ──────────────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A1628] to-[#050B14]" />
        <div className="absolute inset-0 opacity-20"
          style={{ backgroundImage: 'radial-gradient(ellipse at 70% 30%, rgba(239,68,68,0.15) 0%, transparent 55%)' }}
        />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="flex items-center gap-2 mb-4 text-sm">
            <Link to="/phone-repair" className="text-slate-500 hover:text-[#0EA5E9] transition-colors">Phone Repair</Link>
            <ChevronRight className="h-3.5 w-3.5 text-slate-700" />
            <span className="text-slate-400">Data Recovery</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-white leading-tight mb-4">
            Data Recovery Services
          </h1>
          <p className="text-slate-300 text-lg leading-relaxed mb-6 max-w-2xl">
            We recover important photos, videos, files, and documents from damaged and inaccessible devices whenever possible.
            Honest assessment first — if recovery isn't viable, we'll tell you before you pay for anything.
          </p>
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/10 border border-amber-500/30 mb-8">
            <AlertTriangle className="h-4 w-4 text-amber-400 shrink-0" />
            <span className="text-amber-300 text-sm font-medium">Do NOT power on a damaged device — bring it in as-is</span>
          </div>
          <div className="flex flex-wrap gap-3">
            <a href="#intake"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-[#0EA5E9] hover:bg-[#0284C7] text-white font-semibold transition-colors">
              Start a Recovery Request <ArrowRight className="h-4 w-4" />
            </a>
            <a href="tel:+19512168534"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl border border-slate-600 hover:border-[#0EA5E9]/60 text-slate-300 hover:text-white font-semibold transition-colors">
              <Phone className="h-4 w-4" /> (951) 216-8534
            </a>
          </div>
        </div>
      </section>

      {/* ── Supported devices ─────────────────────────────────────────────────── */}
      <section className="py-14 lg:py-18">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2 text-center">Supported Devices</h2>
          <p className="text-slate-400 text-center mb-10">We work with a wide range of devices and storage types.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {DEVICES.map(d => (
              <div key={d.label} className="bg-[#0A1628] border border-slate-700/50 rounded-xl p-5 flex gap-4">
                <div className="w-10 h-10 rounded-lg bg-[#EF4444]/10 border border-[#EF4444]/20 flex items-center justify-center shrink-0">
                  <d.icon className="h-5 w-5 text-[#EF4444]" />
                </div>
                <div>
                  <p className="text-white font-semibold text-sm">{d.label}</p>
                  <p className="text-slate-500 text-xs mt-0.5 leading-relaxed">{d.sub}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── What we recover ───────────────────────────────────────────────────── */}
      <section className="py-12 bg-[#0A1628]/50 border-y border-slate-800/60">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div>
              <h2 className="text-2xl font-bold text-white mb-3">What We Can Recover</h2>
              <p className="text-slate-400 text-sm leading-relaxed mb-5">
                When recovery is possible, we retrieve the data that matters most — files, photos,
                and memories that can't be replaced. We're transparent about what's recoverable
                before any work begins.
              </p>
              <ul className="space-y-2">
                {RECOVERABLE.map(item => (
                  <li key={item} className="flex items-center gap-2 text-sm text-slate-300">
                    <CheckCircle className="h-4 w-4 text-[#10B981] shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="space-y-3">
              <div className="bg-[#050B14] border border-amber-500/20 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="h-4 w-4 text-amber-400" />
                  <span className="text-amber-400 font-semibold text-sm">Important</span>
                </div>
                <p className="text-slate-400 text-sm leading-relaxed">
                  Data recovery success cannot be guaranteed. Results depend on the type and extent of damage,
                  how the device was handled after the incident, and the storage technology involved.
                </p>
              </div>
              <div className="bg-[#050B14] border border-[#0EA5E9]/20 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-2">
                  <Shield className="h-4 w-4 text-[#0EA5E9]" />
                  <span className="text-[#0EA5E9] font-semibold text-sm">No Recovery = No Charge</span>
                </div>
                <p className="text-slate-400 text-sm leading-relaxed">
                  If we can't recover your data, you pay only the diagnostic fee ($29–$49).
                  We never charge for a recovery that didn't happen.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Process ───────────────────────────────────────────────────────────── */}
      <section className="py-14 lg:py-18">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-10 text-center">How It Works</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {PROCESS.map(p => (
              <div key={p.step} className="bg-[#0A1628] border border-slate-700/50 rounded-2xl p-6">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center font-black text-white text-sm mb-4"
                  style={{ background: `linear-gradient(135deg, ${p.color}, ${p.color}80)` }}>
                  {p.step}
                </div>
                <h3 className="text-white font-bold mb-2">{p.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Lead Capture Form ─────────────────────────────────────────────────── */}
      <section id="intake" className="py-14 lg:py-18 bg-[#0A1628]/50 border-t border-slate-800/60">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">Start a Recovery Request</h2>
            <p className="text-slate-400 text-sm">Tell us about your device and we'll reach out to discuss next steps.</p>
          </div>

          {submitted ? (
            <div className="bg-[#050B14] border border-[#10B981]/30 rounded-2xl p-8 text-center">
              <CheckCircle className="h-12 w-12 text-[#10B981] mx-auto mb-3" />
              <h3 className="text-white font-bold text-lg mb-1">Request Received</h3>
              <p className="text-slate-400 text-sm">We'll review your information and reach out shortly to discuss the diagnostic and next steps.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="bg-[#050B14] border border-slate-700/50 rounded-2xl p-6 space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-slate-400 text-sm font-medium mb-1.5">Your Name</label>
                  <input
                    required
                    type="text"
                    value={form.name}
                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    className="w-full bg-[#0A1628] border border-slate-700 rounded-lg px-4 py-2.5 text-white placeholder-slate-600 text-sm focus:outline-none focus:border-[#0EA5E9] transition-colors"
                    placeholder="First & Last Name"
                  />
                </div>
                <div>
                  <label className="block text-slate-400 text-sm font-medium mb-1.5">Phone / Email</label>
                  <input
                    required
                    type="text"
                    value={form.phone}
                    onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                    className="w-full bg-[#0A1628] border border-slate-700 rounded-lg px-4 py-2.5 text-white placeholder-slate-600 text-sm focus:outline-none focus:border-[#0EA5E9] transition-colors"
                    placeholder="Best way to reach you"
                  />
                </div>
              </div>
              <div>
                <label className="block text-slate-400 text-sm font-medium mb-1.5">Device Type & Model</label>
                <input
                  required
                  type="text"
                  value={form.device}
                  onChange={e => setForm(f => ({ ...f, device: e.target.value }))}
                  className="w-full bg-[#0A1628] border border-slate-700 rounded-lg px-4 py-2.5 text-white placeholder-slate-600 text-sm focus:outline-none focus:border-[#0EA5E9] transition-colors"
                  placeholder="e.g. iPhone 15 Pro, MacBook Pro 14 inch, 1TB external drive"
                />
              </div>
              <div>
                <label className="block text-slate-400 text-sm font-medium mb-1.5">What Happened?</label>
                <textarea
                  required
                  rows={4}
                  value={form.issue}
                  onChange={e => setForm(f => ({ ...f, issue: e.target.value }))}
                  className="w-full bg-[#0A1628] border border-slate-700 rounded-lg px-4 py-2.5 text-white placeholder-slate-600 text-sm focus:outline-none focus:border-[#0EA5E9] transition-colors resize-none"
                  placeholder="Describe what happened (dropped in water, won't turn on, files deleted, etc.) and what data you're trying to recover."
                />
              </div>
              <div className="bg-amber-500/8 border border-amber-500/20 rounded-lg p-3">
                <p className="text-amber-300 text-xs leading-relaxed">
                  <strong>Before you bring it in:</strong> Do not attempt to charge, restore, or use recovery apps on a damaged device.
                  Doing so can overwrite data and reduce recovery chances.
                </p>
              </div>
              <button
                type="submit"
                className="w-full py-3 rounded-xl bg-[#0EA5E9] hover:bg-[#0284C7] text-white font-semibold text-sm transition-colors flex items-center justify-center gap-2"
              >
                Submit Recovery Request <ArrowRight className="h-4 w-4" />
              </button>
            </form>
          )}
        </div>
      </section>

      {/* ── FAQ ───────────────────────────────────────────────────────────────── */}
      <section className="py-14 lg:py-18">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-white mb-8 text-center">Data Recovery FAQ</h2>
          <div className="space-y-2">
            {FAQS.map((faq, i) => (
              <div key={i} className="bg-[#0A1628] border border-slate-700/50 rounded-xl overflow-hidden">
                <button
                  className="w-full flex items-center justify-between px-5 py-4 text-left"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                >
                  <span className="text-white font-semibold text-sm pr-4">{faq.q}</span>
                  <span className={`shrink-0 w-5 h-5 rounded-full border border-slate-600 flex items-center justify-center text-slate-400 transition-transform ${openFaq === i ? 'rotate-45' : ''}`}>+</span>
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-4">
                    <p className="text-slate-400 text-sm leading-relaxed">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ───────────────────────────────────────────────────────────────── */}
      <section className="py-12 border-t border-slate-800/60">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold text-white mb-3">Have a device with important data?</h2>
          <p className="text-slate-400 mb-6">The sooner you bring it in, the better the recovery chances.</p>
          <div className="flex flex-wrap justify-center gap-3">
            <a href="#intake"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-[#0EA5E9] hover:bg-[#0284C7] text-white font-bold transition-colors">
              Start a Request <ArrowRight className="h-4 w-4" />
            </a>
            <Link to="/phone-repair"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl border border-slate-600 hover:border-slate-500 text-slate-300 hover:text-white font-semibold transition-colors">
              All Services
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DataRecoveryPage;
